import { useEffect, useState } from "react";
import { Paper, Title, Text, Stack, Menu, ActionIcon, Flex, Skeleton, Group, useMantineTheme } from "@mantine/core";
import * as apiService from "../../api-service";
import { IconCheck, IconDotsVertical, IconBrandApple } from "@tabler/icons";
import IconBadge from "../../components/IconBadge";

export default function ModuleAppleReleases() {
  // Apple's own mark is near-black, which reads fine in light mode but disappears against a
  // dark badge background in dark mode - flip to white there instead, same as the rest of the
  // app already does for text/icons that need to stay visible in both colour schemes.
  const theme = useMantineTheme();
  const APPLE_COLOR = theme.colorScheme === "dark" ? "#ffffff" : "#212529";

  const [isLoading, setIsLoading] = useState(true);
  const [releases, setReleases] = useState([]);

  const allOs = [
    "iOS",
    "iPadOS",
    "watchOS",
    "macOS",
    "tvOS",
    "Xcode"
  ];
  const [currentOs, setCurrentOs] = useState(localStorage.getItem("release_apple_selected_os") ?? "iOS");
  const [showBeta, setShowBeta] = useState(JSON.parse(localStorage.getItem("release_apple_show_beta")) ?? false);

  useEffect(() => {
    apiService
      .getAppleReleases({
        os: currentOs.toLocaleLowerCase(),
        showBeta: showBeta,
      })
      .then((data) => {
        localStorage.setItem("release_apple_show_beta", showBeta);
        setReleases(data);
        setIsLoading(false);
      })
      .catch((data) => {
        console.error(data);
        setIsLoading(false);
      });
  }, [currentOs, showBeta]);

  // Release titles come back as e.g. "iOS 18.1 (22B83)" - strip the trailing build number for
  // display. When there's no title at all (data unavailable), lastIndexOf("(") returns -1, which
  // used to make substring(0, -1) collapse to an empty string instead of showing "Unavailable" -
  // guarded against explicitly so the fallback text actually renders.
  const rawTitle = releases.length > 0 ? releases[0]?.title : "Unavailable";
  const parenIndex = rawTitle?.lastIndexOf("(") ?? -1;
  const displayTitle = parenIndex > -1 ? rawTitle.substring(0, parenIndex).trim() : rawTitle;

  return (
    <Paper shadow="sm" radius="md" p="md" withBorder>
      <Group spacing="sm" noWrap align="flex-start">
        <IconBadge color={APPLE_COLOR} icon={<IconBrandApple size={20} stroke={2.5} color={APPLE_COLOR} />} />
        <Stack spacing={0} sx={{ minWidth: 0, flex: 1 }}>
          <Text size="xs" fw={700} tt="uppercase" color="dimmed" sx={{ letterSpacing: 0.4 }}>
            Latest {currentOs} Release
          </Text>
          {isLoading && (
            <Stack spacing="xs" p={2}>
              <Skeleton height={28} radius="sm" />
              <Skeleton height={18} radius="sm" />
            </Stack>
          )}
          {!isLoading && (
            <Flex justify="space-between" align="center" gap={16}>
              {/* No line clamp - wraps onto a second line instead of being cut off, rather than
                  hiding part of the version string. */}
              <Title order={2} weight={800}>
                {displayTitle}
              </Title>

              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <ActionIcon size="lg" disabled={isLoading}>
                    <IconDotsVertical size={28} />
                  </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Label>Select Operating System</Menu.Label>
                  {allOs.map((i) => {
                    return (
                      <Menu.Item
                        key={i}
                        icon={currentOs === i ? <IconCheck size={16} /> : <></>}
                        disabled={currentOs === i}
                        onClick={() => {
                          setCurrentOs(i);
                          localStorage.setItem("release_apple_selected_os", i);
                        }}
                      >
                        {i}
                      </Menu.Item>
                    );
                  })}
                  <Menu.Divider />
                  <Menu.Label>Build Types</Menu.Label>
                  <Menu.Item icon={showBeta ? <IconCheck size={16} /> : <></>} onClick={() => setShowBeta(!showBeta)}>
                    Show Betas/RC
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Flex>
          )}
          {!isLoading && (
            <Title order={5} weight={300} color="dimmed">
              {releases[0]?.pubDate ? releases[0].pubDate.substring(0, 17) : "Release date unavailable"}
            </Title>
          )}
        </Stack>
      </Group>
    </Paper>
  );
}
